import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown, Cpu, Sparkles, Wand2, PlayCircle, PauseCircle, SkipBack, SkipForward,
  Scissors, Trash2, MagnetIcon, ZoomIn, Download, Radio, Loader2, RefreshCw,
  Film, LayoutList, Settings2, Volume2, Music, VideoIcon, Send, ImagePlus, Layers,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const RATIOS = ["1280:720", "720:1280", "1104:832", "832:1104", "960:960", "1584:672"];
const RESOLUTIONS = [
  { key: "1080p", label: "1080p" },
  { key: "2k", label: "2K" },
  { key: "4k", label: "4K HDR" },
];
const NAV_TABS = [
  { key: "timeline", label: "Timeline Editing", icon: Film },
  { key: "generations", label: "Generations", icon: LayoutList },
  { key: "cuts", label: "Cuts", icon: Scissors },
  { key: "vibe", label: "Vibe Director Studio", icon: Sparkles },
];

async function api(path, token, opts = {}) {
  const res = await fetch(`${API}/api${path}`, {
    headers: {
      "X-Admin-Token": token,
      ...(opts.body ? { "Content-Type": "application/json" } : {}),
    },
    method: opts.method || "GET",
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
  return data;
}

const ENGINE_TAG = {
  runway_cloud: { label: "RUNWAY 4.5", color: "text-teal-300", bg: "bg-teal-500/15", border: "border-teal-500/50" },
  local_gpu: { label: "LTX 2.5", color: "text-fuchsia-300", bg: "bg-fuchsia-500/15", border: "border-fuchsia-500/50" },
  local_remote: { label: "MINIMAX 3H", color: "text-amber-300", bg: "bg-amber-500/15", border: "border-amber-500/50" },
  emergent: { label: "EMERGENT AI", color: "text-emerald-300", bg: "bg-emerald-500/15", border: "border-emerald-500/50" },
  custom: { label: "CUSTOM", color: "text-slate-300", bg: "bg-slate-500/15", border: "border-slate-500/50" },
};

const STATUS_META = {
  PENDING: { color: "text-slate-300", tag: "PENDING", ring: "ring-slate-500" },
  THROTTLED: { color: "text-amber-300", tag: "QUEUED", ring: "ring-amber-500" },
  RUNNING: { color: "text-teal-300", tag: "RENDERING", ring: "ring-teal-500" },
  SUCCEEDED: { color: "text-emerald-300", tag: "READY", ring: "ring-emerald-500" },
  FAILED: { color: "text-rose-300", tag: "FAILED", ring: "ring-rose-500" },
  CANCELLED: { color: "text-slate-500", tag: "CANCELLED", ring: "ring-slate-600" },
};

export default function QuantumVideoStudio({ token: propToken }) {
  const [token] = useState(() => propToken || localStorage.getItem("admin_token") || "dwig-admin-2025");
  const [config, setConfig] = useState(null);
  const [telemetry, setTelemetry] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [engine, setEngine] = useState("runway_cloud");
  const [engineOpen, setEngineOpen] = useState(false);
  const [tab, setTab] = useState("timeline");
  const [drawer, setDrawer] = useState("vibe"); // starters | vibe
  const [resolution, setResolution] = useState("1080p");
  const [prompt, setPrompt] = useState("");
  const [imageUri, setImageUri] = useState("");
  const [model, setModel] = useState("gen4.5");
  const [ratio, setRatio] = useState("1280:720");
  const [duration, setDuration] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const [engineOverride, setEngineOverride] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [snap, setSnap] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [currentSec, setCurrentSec] = useState(0);
  const timerRef = useRef(null);

  // Load config + telemetry + tasks and poll
  const load = useCallback(async () => {
    try {
      const [c, t, tasksRes] = await Promise.all([
        config ? null : api("/admin/video-studio/config", token),
        api("/admin/video-studio/telemetry", token),
        api("/admin/video-studio/tasks?limit=40", token),
      ]);
      if (c) setConfig(c);
      setTelemetry(t);
      setTasks(tasksRes.tasks || []);
    } catch (e) {
      console.error(e);
    }
  }, [token, config]);

  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);

  // Poll any in-flight tasks
  useEffect(() => {
    const inFlight = tasks.filter((t) => ["PENDING", "THROTTLED", "RUNNING"].includes(t.status));
    if (inFlight.length === 0) return;
    const t = setInterval(() => {
      inFlight.forEach(async (task) => {
        try { await api(`/admin/video-studio/tasks/${task.task_id}`, token); }
        catch { /* ignore */ }
      });
    }, 6000);
    return () => clearInterval(t);
  }, [tasks, token]);

  // Playhead ticker
  useEffect(() => {
    if (!playing) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => setCurrentSec((s) => s + 0.1), 100);
    return () => clearInterval(timerRef.current);
  }, [playing]);

  const succeeded = useMemo(() => tasks.filter((t) => t.status === "SUCCEEDED"), [tasks]);
  const timelineClips = useMemo(() => succeeded.slice(0, 8), [succeeded]);
  const totalTimelineSec = useMemo(() => timelineClips.reduce((a, t) => a + (t.request?.duration || 5), 0) || 40, [timelineClips]);

  const submit = async () => {
    if (submitting) return;
    if (!prompt.trim() && !imageUri.trim()) return;
    setSubmitting(true);
    try {
      const body = {
        model,
        ratio,
        duration,
        engine_id: "runway_cloud",
        ...(prompt.trim() ? { promptText: prompt.trim() } : {}),
        ...(imageUri.trim() ? { promptImage: { uri: imageUri.trim(), position: "first" } } : {}),
      };
      // If image-only + no prompt on gen4.5 → force gen4_turbo
      if (!prompt.trim() && imageUri.trim()) body.model = "gen4_turbo";
      const created = await api("/admin/video-studio/generate", token, { method: "POST", body });
      setSelectedTask(created.task_id);
      setPrompt("");
      setImageUri("");
      await load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const applyStarter = (s) => {
    setPrompt(s.prompt);
    setModel(s.model);
    setRatio(s.ratio);
    setDuration(s.duration);
    setDrawer("vibe");
  };

  const deleteTask = async (id) => {
    if (!window.confirm("Delete this generation?")) return;
    try { await api(`/admin/video-studio/tasks/${id}`, token, { method: "DELETE" }); await load(); }
    catch (e) { alert(e.message); }
  };

  const preview = selectedTask ? tasks.find((t) => t.task_id === selectedTask) : succeeded[0];
  const previewUrl = preview?.output?.[0];

  return (
    <div className="bg-zinc-950 text-zinc-100 min-h-screen font-sans">
      {/* HEADER */}
      <header className="border-b border-zinc-800/80 bg-zinc-950/95 backdrop-blur px-5 py-3 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
          <span className="text-[10px] font-bold tracking-[0.3em] text-teal-300">QUANTUM VIDEO STUDIO</span>
        </div>

        {/* Engine dropdown */}
        <div className="relative">
          <button
            data-testid="engine-select"
            onClick={() => setEngineOpen((o) => !o)}
            className="flex items-center gap-2 border border-zinc-800 hover:border-teal-500/60 bg-zinc-900/60 px-3 py-1.5 rounded-lg text-xs font-semibold transition"
          >
            <Cpu size={13} className="text-teal-300" />
            {config?.engines.find((e) => e.id === engine)?.label || "Runway API (Cloud)"}
            <ChevronDown size={13} className={`transition ${engineOpen ? "rotate-180" : ""}`} />
          </button>
          <AnimatePresence>
            {engineOpen && config ? (
              <motion.div
                initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="absolute top-full left-0 mt-1 w-80 bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl overflow-hidden z-50"
              >
                {config.engines.map((e) => {
                  const active = engine === e.id;
                  return (
                    <button
                      key={e.id}
                      data-testid={`engine-${e.id}`}
                      onClick={() => { setEngine(e.id); setEngineOpen(false); }}
                      className={`w-full text-left px-3 py-2 border-l-2 transition ${
                        active ? "bg-teal-500/10 border-teal-500" : "border-transparent hover:bg-zinc-800/60"
                      }`}
                    >
                      <div className="flex items-center gap-2 text-xs font-semibold">
                        <span className={active ? "text-teal-300" : "text-zinc-100"}>{e.label}</span>
                        <span className={`ml-auto text-[9px] font-bold tracking-[0.15em] ${e.status === "ONLINE" ? "text-teal-300" : "text-zinc-500"}`}>{e.status}</span>
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{e.note}</div>
                    </button>
                  );
                })}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>

        {/* Telemetry */}
        <div className="flex items-center gap-4 ml-auto text-[10px] font-mono tracking-wide">
          <Telemetry label="VRAM" value={telemetry ? `${telemetry.vram_used_gb} / ${telemetry.vram_total_gb} GB` : "…"} accent="text-teal-300" />
          <Telemetry label="LATENCY" value={telemetry ? `${telemetry.inference_latency_ms}ms` : "…"} accent="text-fuchsia-300" />
          <Telemetry label="QUEUED" value={telemetry ? String(telemetry.queued_jobs) : "…"} accent="text-amber-300" />
          <Telemetry label="STATUS" value={telemetry?.status || "…"} accent="text-emerald-300" icon={<Radio size={11} />} />
        </div>
      </header>

      {/* NAV TABS */}
      <div className="border-b border-zinc-800/80 px-5 py-2 flex items-center gap-1">
        {NAV_TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`vs-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 text-[11px] font-bold tracking-wide px-3 py-1.5 rounded-md transition ${
                active ? "bg-zinc-800 text-teal-300" : "text-zinc-400 hover:text-zinc-100"
              }`}
            >
              <Icon size={12} /> {t.label.toUpperCase()}
            </button>
          );
        })}
      </div>

      {/* MAIN grid: viewport + right drawer */}
      <div className="grid grid-cols-[1fr_360px] gap-3 p-3 h-[calc(100vh-260px)]">
        {/* VIEWPORT */}
        <div className="flex flex-col gap-3 min-w-0">
          <div className="flex-1 min-h-0 bg-black rounded-lg border border-zinc-800/80 overflow-hidden relative">
            <div className="absolute inset-0 flex items-center justify-center">
              {previewUrl ? (
                <video
                  key={previewUrl}
                  src={previewUrl}
                  controls={false}
                  autoPlay={playing}
                  loop
                  className="max-h-full max-w-full"
                  data-testid="vs-preview-video"
                />
              ) : (
                <div className="text-center">
                  <VideoIcon size={40} className="text-zinc-700 mx-auto" />
                  <p className="text-xs text-zinc-500 mt-3">
                    {tasks.some((t) => ["PENDING", "THROTTLED", "RUNNING"].includes(t.status))
                      ? "Rendering in progress…"
                      : "Submit a prompt to render."}
                  </p>
                </div>
              )}
            </div>
            {preview ? (
              <div className={`absolute top-3 left-3 flex items-center gap-2 px-2 py-1 rounded-md bg-black/60 backdrop-blur border ${STATUS_META[preview.status]?.ring} ring-1`}>
                <span className={`text-[10px] font-bold tracking-[0.2em] ${STATUS_META[preview.status]?.color}`}>
                  {STATUS_META[preview.status]?.tag || preview.status}
                </span>
                <span className={`text-[9px] font-bold tracking-[0.15em] ${ENGINE_TAG[preview.engine_id]?.color}`}>
                  {ENGINE_TAG[preview.engine_id]?.label}
                </span>
              </div>
            ) : null}
          </div>

          {/* Player row */}
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-zinc-800/80 bg-zinc-900/60">
            <button data-testid="vs-play" onClick={() => setPlaying((p) => !p)} className="text-teal-300 hover:text-teal-200">
              {playing ? <PauseCircle size={22} /> : <PlayCircle size={22} />}
            </button>
            <button className="text-zinc-400 hover:text-zinc-100" onClick={() => setCurrentSec((s) => Math.max(0, s - 0.1))}>
              <SkipBack size={16} />
            </button>
            <button className="text-zinc-400 hover:text-zinc-100" onClick={() => setCurrentSec((s) => s + 0.1)}>
              <SkipForward size={16} />
            </button>
            <div className="font-mono text-[11px] text-zinc-300 tabular-nums">
              {fmtTC(currentSec)} <span className="text-zinc-600">/</span> {fmtTC(totalTimelineSec)}
            </div>
            <div className="ml-auto flex items-center gap-1">
              {RESOLUTIONS.map((r) => (
                <button
                  key={r.key}
                  data-testid={`vs-res-${r.key}`}
                  onClick={() => setResolution(r.key)}
                  className={`text-[10px] font-bold tracking-widest px-2 py-1 rounded transition ${
                    resolution === r.key ? "bg-zinc-800 text-teal-300" : "text-zinc-500 hover:text-zinc-100"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT DRAWER */}
        <div className="bg-zinc-900/60 border border-zinc-800/80 rounded-lg flex flex-col overflow-hidden">
          <div className="flex border-b border-zinc-800/80">
            {[
              { key: "starters", label: "Starters & Skills", icon: Layers },
              { key: "vibe", label: "Vibe Command", icon: Wand2 },
            ].map((d) => {
              const Icon = d.icon;
              const active = drawer === d.key;
              return (
                <button
                  key={d.key}
                  data-testid={`vs-drawer-${d.key}`}
                  onClick={() => setDrawer(d.key)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-bold tracking-wide transition ${
                    active ? "text-teal-300 bg-zinc-950" : "text-zinc-400 hover:text-zinc-100"
                  }`}
                >
                  <Icon size={12} /> {d.label.toUpperCase()}
                </button>
              );
            })}
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {drawer === "starters" ? (
              (config?.starters || []).map((s) => (
                <button
                  key={s.id}
                  data-testid={`vs-starter-${s.id}`}
                  onClick={() => applyStarter(s)}
                  className="w-full text-left border border-zinc-800 hover:border-teal-500/60 bg-zinc-950/70 rounded-lg p-3 transition"
                >
                  <div className="flex items-center gap-2 text-xs font-bold text-zinc-100">
                    /{s.label}
                    <span className={`ml-auto text-[9px] font-bold tracking-[0.15em] ${ENGINE_TAG[s.model === "gen4.5" ? "runway_cloud" : "runway_cloud"].color}`}>
                      {s.model.toUpperCase()} · {s.ratio} · {s.duration}s
                    </span>
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1 leading-snug line-clamp-2">{s.prompt}</p>
                </button>
              ))
            ) : (
              <>
                <label className="text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase block">Scene direction</label>
                <textarea
                  data-testid="vs-prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={5}
                  maxLength={1000}
                  placeholder="e.g. Slow cinematic dolly toward a lighthouse at sunset, ocean mist moving naturally"
                  className="w-full rounded-lg bg-zinc-950 border border-zinc-800 focus:border-teal-500 outline-none px-2.5 py-2 text-xs text-zinc-100"
                />
                <label className="text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase block mt-2 flex items-center gap-1">
                  <ImagePlus size={11} /> Prompt image URL (optional)
                </label>
                <input
                  data-testid="vs-image"
                  value={imageUri}
                  onChange={(e) => setImageUri(e.target.value)}
                  placeholder="https://…jpg"
                  className="w-full rounded-lg bg-zinc-950 border border-zinc-800 focus:border-teal-500 outline-none px-2.5 py-1.5 text-xs text-zinc-100"
                />

                <div className="grid grid-cols-2 gap-2 mt-3">
                  <SelectRow label="Model" value={model} onChange={setModel} options={[{v:"gen4.5",l:"Gen-4.5"},{v:"gen4_turbo",l:"Gen-4 Turbo"}]} testId="vs-model" />
                  <SelectRow label="Ratio" value={ratio} onChange={setRatio} options={RATIOS.map((r) => ({ v: r, l: r }))} testId="vs-ratio" />
                  <SelectRow label="Duration" value={String(duration)} onChange={(v) => setDuration(Number(v))} options={[{v:"3",l:"3s"},{v:"5",l:"5s"},{v:"10",l:"10s"}]} testId="vs-duration" />
                  <ToggleRow label="Engine Override" value={engineOverride} onChange={setEngineOverride} testId="vs-override" />
                </div>

                <button
                  data-testid="vs-submit"
                  onClick={submit}
                  disabled={submitting || (!prompt.trim() && !imageUri.trim())}
                  className="mt-3 w-full flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold text-xs tracking-wide py-2.5 rounded-lg transition"
                >
                  {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} DISPATCH TO RUNWAY
                </button>
                {engineOverride ? (
                  <p className="text-[10px] text-fuchsia-300 mt-2">Engine override enabled — route characters to MiniMax 3H, wides to Runway 4.5 (currently forced to Runway; local engines on standby).</p>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      {/* TIMELINE / BOTTOM DOCK */}
      <div className="border-t border-zinc-800/80 bg-zinc-950 px-4 py-3">
        <div className="flex items-center gap-3 mb-2">
          <button className="text-zinc-400 hover:text-teal-300 flex items-center gap-1 text-[11px] font-bold tracking-wide"><Scissors size={12} /> SPLIT</button>
          <button className="text-zinc-400 hover:text-rose-300 flex items-center gap-1 text-[11px] font-bold tracking-wide"><Trash2 size={12} /> RIPPLE DELETE</button>
          <button
            onClick={() => setSnap(!snap)}
            className={`flex items-center gap-1 text-[11px] font-bold tracking-wide ${snap ? "text-teal-300" : "text-zinc-400 hover:text-zinc-100"}`}
          >
            <MagnetIcon size={12} /> SNAPPING {snap ? "ON" : "OFF"}
          </button>
          <div className="flex items-center gap-1 text-zinc-400">
            <ZoomIn size={12} />
            <input type="range" min="0.5" max="3" step="0.1" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="w-24 accent-teal-500" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button className="text-[11px] font-bold tracking-wide text-zinc-400 hover:text-zinc-100 flex items-center gap-1"><Settings2 size={12} /> SETTINGS</button>
            <button className="text-[11px] font-bold tracking-wide bg-teal-500 hover:bg-teal-400 text-black px-3 py-1.5 rounded flex items-center gap-1"><Download size={12} /> EXPORT</button>
          </div>
        </div>

        {/* Tracks */}
        <div className="rounded-lg border border-zinc-800/80 overflow-hidden">
          <Track label="V1" icon={<VideoIcon size={11} />} accent="teal">
            {timelineClips.length === 0 ? (
              <div className="text-[10px] text-zinc-600 px-3 py-2 italic">No clips yet — dispatch a prompt to fill the timeline.</div>
            ) : (
              <div className="flex" style={{ transform: `scaleX(${zoom})`, transformOrigin: "left" }}>
                {timelineClips.map((c, idx) => {
                  const w = (c.request?.duration || 5) * 60;
                  return (
                    <div
                      key={c.task_id}
                      data-testid={`clip-${c.task_id}`}
                      onClick={() => setSelectedTask(c.task_id)}
                      className={`relative shrink-0 h-[64px] border-r border-zinc-900 cursor-pointer overflow-hidden group ${
                        selectedTask === c.task_id ? "ring-2 ring-teal-500" : ""
                      }`}
                      style={{ width: `${w}px`, background: `linear-gradient(120deg, rgba(20,184,166,0.35), rgba(147,51,234,0.25))` }}
                    >
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.15),transparent_40%)]" />
                      <div className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[8px] font-bold tracking-[0.1em] ${ENGINE_TAG[c.engine_id]?.bg} ${ENGINE_TAG[c.engine_id]?.color}`}>
                        [{ENGINE_TAG[c.engine_id]?.label}]
                      </div>
                      <div className="absolute bottom-1 left-1 right-1 text-[9px] text-zinc-100 font-semibold truncate">
                        Clip #{idx + 1} · {(c.request?.duration || 5)}s
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Track>
          <Track label="A1" icon={<Volume2 size={11} />} accent="fuchsia">
            <WaveformStrip clips={timelineClips.length} tint="fuchsia" />
          </Track>
          <Track label="A2" icon={<Music size={11} />} accent="amber" last>
            <WaveformStrip clips={timelineClips.length} tint="amber" />
          </Track>
        </div>

        {/* Playhead */}
        <div className="mt-2 relative h-1 bg-zinc-900 rounded">
          <div
            className="absolute top-0 h-full bg-teal-400"
            style={{ width: `${Math.min(100, (currentSec / totalTimelineSec) * 100)}%` }}
          />
        </div>

        {/* Generations strip (secondary browser) */}
        {tab === "generations" ? (
          <div className="mt-3 grid grid-cols-6 gap-2">
            {tasks.map((t) => (
              <div
                key={t.task_id}
                data-testid={`gen-${t.task_id}`}
                onClick={() => setSelectedTask(t.task_id)}
                className={`relative aspect-video rounded-md border border-zinc-800 overflow-hidden cursor-pointer hover:border-teal-500/60 transition ${selectedTask === t.task_id ? "ring-2 ring-teal-500" : ""}`}
              >
                {t.output?.[0] ? (
                  <video src={t.output[0]} muted className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-zinc-900">
                    {["PENDING", "THROTTLED", "RUNNING"].includes(t.status) ? (
                      <Loader2 size={16} className="animate-spin text-teal-300" />
                    ) : (
                      <VideoIcon size={16} className="text-zinc-700" />
                    )}
                  </div>
                )}
                <div className={`absolute top-1 left-1 text-[8px] font-bold tracking-[0.1em] px-1 py-0.5 rounded ${ENGINE_TAG[t.engine_id]?.bg} ${ENGINE_TAG[t.engine_id]?.color}`}>
                  {ENGINE_TAG[t.engine_id]?.label}
                </div>
                <div className={`absolute bottom-1 right-1 text-[8px] font-bold tracking-[0.1em] ${STATUS_META[t.status]?.color}`}>
                  {STATUS_META[t.status]?.tag}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteTask(t.task_id); }}
                  className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-rose-300 hover:text-rose-200"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Telemetry({ label, value, accent, icon }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-zinc-500 uppercase font-bold tracking-[0.2em]">{icon}{label}</span>
      <span className={`${accent} font-bold`}>{value}</span>
    </div>
  );
}

function Track({ label, icon, accent, children, last }) {
  const c = { teal: "text-teal-300", fuchsia: "text-fuchsia-300", amber: "text-amber-300" }[accent];
  return (
    <div className={`flex ${last ? "" : "border-b border-zinc-900"}`}>
      <div className={`w-14 shrink-0 bg-zinc-900/60 flex items-center justify-center text-[10px] font-bold tracking-widest ${c}`}>
        {icon}<span className="ml-1">{label}</span>
      </div>
      <div className="flex-1 min-h-[64px] bg-black/60 relative overflow-x-auto">
        {children}
      </div>
    </div>
  );
}

function WaveformStrip({ clips, tint }) {
  if (clips === 0) return <div className="text-[10px] text-zinc-600 px-3 py-2 italic">Waveform will render on export.</div>;
  const colorMap = { fuchsia: "rgba(217,70,239,0.55)", amber: "rgba(245,158,11,0.55)" };
  return (
    <svg viewBox="0 0 1000 64" preserveAspectRatio="none" className="w-full h-full">
      {Array.from({ length: 200 }).map((_, i) => {
        const h = Math.abs(Math.sin(i * 0.35) * 20) + Math.abs(Math.cos(i * 0.13) * 12) + 3;
        return <rect key={i} x={i * 5} y={32 - h / 2} width={3} height={h} fill={colorMap[tint]} />;
      })}
    </svg>
  );
}

function SelectRow({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase block mb-1">{label}</label>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg bg-zinc-950 border border-zinc-800 outline-none focus:border-teal-500 px-2 py-1.5 text-xs text-zinc-100"
      >
        {options.map((o) => (<option key={o.v} value={o.v}>{o.l}</option>))}
      </select>
    </div>
  );
}

function ToggleRow({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[10px] font-bold tracking-[0.2em] text-zinc-400 uppercase block mb-1">{label}</label>
      <button
        data-testid={testId}
        onClick={() => onChange(!value)}
        className={`w-full py-1.5 rounded-lg border text-[11px] font-bold tracking-wide transition ${
          value ? "border-fuchsia-500 bg-fuchsia-500/20 text-fuchsia-200" : "border-zinc-800 text-zinc-400"
        }`}
      >
        {value ? "ON" : "OFF"}
      </button>
    </div>
  );
}

function fmtTC(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
